import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@boveda/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "lucide-solid";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Cuentas conectadas": permite vincular/desvincular Google y Apple desde el
 * perfil. Solo muestra providers HABILITADOS en el backend. Respeta la
 * salvaguarda del último método (el backend rechaza dejar al usuario sin acceso).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Lee ?linked=<provider> para mostrar feedback tras volver del callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => {
      const { data } = await sharedApi.socialProviders(api);
      return data ?? { google: false, apple: false };
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => {
      const { data } = await sharedApi.loginMethods(api);
      return data ?? { providers: [], has_password: false };
    },
    retry: false,
  }));

  const enabledProviders = createMemo<ProviderId[]>(() => {
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  const linkedProviders = createMemo(() => methodsQuery.data?.providers ?? []);
  const hasPassword = createMemo(() => methodsQuery.data?.has_password ?? false);

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Número de métodos de acceso totales (password + cada social vinculada).
  const totalMethods = createMemo(() => linkedProviders().length);

  // ¿Es este el ÚLTIMO método? (no se puede desvincular sin establecer otro).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback al volver del flujo de vinculación (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          <Show
            when={!methodsQuery.isPending}
            fallback={
              <div class="space-y-3">
                <Skeleton class="h-14 w-full rounded-xl" />
                <Skeleton class="h-14 w-full rounded-xl" />
              </div>
            }
          >
            <For each={enabledProviders()}>
              {(provider) => (
                <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Link2 class="h-5 w-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                      }
                    >
                      <Badge
                        variant="secondary"
                        class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                      >
                        {t(msg`Connected`)}
                      </Badge>
                    </Show>
                  </div>

                  <Show
                    when={isLinked(provider)}
                    fallback={
                      <a
                        href={linkUrl(provider)}
                        class={buttonVariants({ variant: "outline", size: "sm" })}
                        data-testid={`link-${provider}`}
                      >
                        {t(msg`Connect`)}
                      </a>
                    }
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy() || isLastMethod(provider)}
                      title={
                        isLastMethod(provider)
                          ? t(msg`Set a password before unlinking your only sign-in method.`)
                          : undefined
                      }
                      onClick={() => setConfirmUnlink(provider)}
                      class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      {t(msg`Disconnect`)}
                    </Button>
                  </Show>
                </div>
              )}
            </For>

            <Show when={!hasPassword() && totalMethods() <= 1}>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                )}
              </p>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
